import React, { useState } from 'react';

const C = {
  bg: '#0a0a0c', bg2: '#111114', bg3: '#18181c',
  gold: '#c9a84c', goldL: '#e8c97a',
  sage: '#7aab8a', rose: '#c47a7a', sky: '#7a9ec4', lav: '#9a7ac4',
  border: 'rgba(201,168,76,0.15)',
  text: '#f0ede6', muted: 'rgba(240,237,230,0.5)', dim: 'rgba(240,237,230,0.25)',
};

const SECTIONS = ['Criteria', 'IV Rank', 'Liquidity', 'TOS Scan', 'Avoid', 'Quiz'];

const QUIZ = [
  {
    q: 'What is the minimum stock price you should target for The Wheel?',
    options: ['$5', '$10', '$20', '$50'],
    correct: 2,
    explain: '$20 minimum ensures meaningful premium. Below that, the credit collected barely covers commissions and risk.',
  },
  {
    q: 'An IV Rank of 65 means:',
    options: [
      'IV is at 65% of its 52-week range — elevated, good for selling.',
      'The stock will move 65% in the next year.',
      'You should avoid selling premium right now.',
      'The stock has 65 days until earnings.',
    ],
    correct: 0,
    explain: 'IV Rank compares current IV to its 52-week range. 65 means IV is historically elevated — ideal for selling premium.',
  },
  {
    q: 'Which of these makes a stock UNSUITABLE for The Wheel?',
    options: [
      'IV Rank above 30',
      'Earnings within the next 7 days',
      'Price between $30–$100',
      'Open interest above 500 contracts',
    ],
    correct: 1,
    explain: 'Never sell options into earnings without intending to hold through the binary event. IV collapses after earnings — and price can gap violently.',
  },
  {
    q: "On Thinkorswim's Stock Hacker scan, what does \"OI > 500\" filter for?",
    options: [
      'Stocks with earnings in the next 500 days',
      'Stocks with options that have at least 500 open interest contracts — ensuring liquidity',
      'Stocks with a price above $500',
      'Stocks with 500% IV',
    ],
    correct: 1,
    explain: 'Open Interest (OI) measures how many contracts exist. Higher OI = tighter spreads, easier fills, less slippage.',
  },
  {
    q: "A stock's bid/ask spread on the option is $0.85 / $1.40. What does this tell you?",
    options: [
      'The option is very liquid — tight spread.',
      'The spread is $0.55 wide — acceptable for a $50 stock, concerning for a $15 stock.',
      'You should always take the midpoint.',
      'B and C are both correct.',
    ],
    correct: 3,
    explain: "A $0.55 spread is wide. As a seller, aim for the mid or better. For cheap stocks, this spread can eat most of your premium.",
  },
];

const card = { background: C.bg2, border: `0.5px solid ${C.border}`, borderRadius: '10px', padding: '20px 22px', marginBottom: '14px' };

const tagStyle = (color, bg) => ({
  fontSize: '10px', padding: '3px 10px', borderRadius: '4px',
  background: bg, color, letterSpacing: '0.07em', fontWeight: 500, display: 'inline-block',
});

export default function StockSelectionChapter({ onComplete, xpAward }) {
  const [section, setSection]     = useState(0);
  const [checklist, setChecklist] = useState({});
  const [answers, setAnswers]     = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [score, setScore]         = useState(null);

  const toggleCheck = (key) => setChecklist(c => ({ ...c, [key]: !c[key] }));

  const submitQuiz = () => {
    let correct = 0;
    QUIZ.forEach((q, i) => { if (answers[i] === q.correct) correct++; });
    const pct = Math.round((correct / QUIZ.length) * 100);
    setScore(pct);
    setSubmitted(true);
    const xp = pct >= 75 ? 120 : 40;
    xpAward && xpAward(xp);
    if (pct >= 75) onComplete && onComplete({ quizScore: pct });
  };

  const navBtn = (active) => ({
    padding: '7px 14px', borderRadius: '7px', cursor: 'pointer',
    border: `0.5px solid ${active ? C.gold : C.border}`,
    background: active ? 'rgba(201,168,76,0.1)' : 'transparent',
    color: active ? C.goldL : C.muted,
    fontSize: '12px', fontWeight: active ? 500 : 400, letterSpacing: '0.04em',
  });

  return (
    <div style={{ fontFamily: "'Jost','Outfit',sans-serif", background: C.bg, color: C.text, minHeight: '100vh', padding: '28px 20px', maxWidth: '780px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ fontSize: '11px', letterSpacing: '0.14em', color: C.gold, textTransform: 'uppercase', marginBottom: '6px' }}>♀ Chapter: Stock Selection</div>
        <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '28px', fontWeight: 500, margin: '0 0 4px' }}>How to Choose Stocks for The Wheel</h2>
        <p style={{ fontSize: '13px', color: C.muted, margin: 0 }}>Not every stock belongs in your portfolio. Here's how to find the right ones.</p>
      </div>

      {/* Section nav */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '24px', flexWrap: 'wrap' }}>
        {SECTIONS.map((s, i) => (
          <button key={s} style={navBtn(section === i)} onClick={() => setSection(i)}>{s}</button>
        ))}
      </div>

      {/* Section 0: Criteria Checklist */}
      {section === 0 && (
        <div>
          <div style={card}>
            <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '20px', marginBottom: '16px' }}>The Wheel Stock Checklist</div>
            <p style={{ fontSize: '13px', color: C.muted, marginBottom: '20px', lineHeight: 1.7 }}>
              Before you sell a single option, run every candidate through this checklist. If it fails more than one of these, skip it and find a better stock.
            </p>
            {[
              { key: 'price',    label: 'Price ≥ $20',                              note: "Too cheap = too little premium. Under $20, commissions eat your edge." },
              { key: 'ivrank',   label: 'IV Rank ≥ 30 (ideally 30–60)',             note: "Elevated IV means fatter premium. You're collecting more for the same risk." },
              { key: 'oi',       label: 'Open Interest ≥ 500 contracts',            note: "Liquidity. You need to be able to exit without getting crushed on the spread." },
              { key: 'bid_ask',  label: 'Bid/ask spread ≤ $0.15 per $1 of premium', note: "Wide spreads kill your edge. Always check before placing." },
              { key: 'earnings', label: 'No earnings within 30 days',               note: "Earnings = binary risk. IV spikes before, collapses after. Avoid." },
              { key: 'trend',    label: 'Neutral to bullish trend',                  note: "You'll potentially own this stock. Make sure you'd be comfortable holding it." },
              { key: 'own',      label: "You'd be proud to own shares",              note: "This is the Wheel key question. If the stock dropped 20%, would you hold or panic?" },
              { key: 'support',  label: 'Identifiable support level below strike',   note: "Your strike should be at or below a meaningful technical support level." },
            ].map(({ key, label, note }) => (
              <div key={key} style={{ display: 'flex', gap: '12px', padding: '12px 0', borderBottom: `0.5px solid rgba(240,237,230,0.05)`, cursor: 'pointer', alignItems: 'flex-start' }}
                onClick={() => toggleCheck(key)}>
                <div style={{ width: '20px', height: '20px', borderRadius: '4px', border: `1px solid ${checklist[key] ? C.sage : 'rgba(240,237,230,0.2)'}`, background: checklist[key] ? 'rgba(122,171,138,0.15)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '2px', fontSize: '12px', color: C.sage }}>
                  {checklist[key] ? '✓' : ''}
                </div>
                <div>
                  <div style={{ fontSize: '14px', fontWeight: 500, marginBottom: '3px', color: checklist[key] ? C.sage : C.text }}>{label}</div>
                  <div style={{ fontSize: '12px', color: C.muted, lineHeight: 1.5 }}>{note}</div>
                </div>
              </div>
            ))}
            <div style={{ marginTop: '14px', fontSize: '12px', color: C.gold }}>
              {Object.values(checklist).filter(Boolean).length} / 8 criteria checked
            </div>
          </div>
        </div>
      )}

      {/* Section 1: IV Rank */}
      {section === 1 && (
        <div>
          <div style={card}>
            <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '20px', marginBottom: '12px' }}>Understanding IV Rank</div>
            <p style={{ fontSize: '14px', color: C.muted, lineHeight: 1.8, marginBottom: '20px' }}>
              IV Rank tells you where current Implied Volatility sits relative to its own 52-week range. It's the single most important filter for option sellers.
            </p>
            <div style={{ background: C.bg3, borderRadius: '8px', padding: '16px', marginBottom: '20px', fontFamily: "'DM Mono', monospace", fontSize: '13px' }}>
              IV Rank = (Current IV − 52wk Low IV) ÷ (52wk High IV − 52wk Low IV) × 100
            </div>
            <p style={{ fontSize: '13px', color: C.muted, marginBottom: '20px', lineHeight: 1.7 }}>
              Example: If a stock's IV range was 18–52% over the past year, and today's IV is 44%, then IV Rank = (44−18)÷(52−18)×100 = <strong style={{ color: C.goldL }}>76</strong>. That's high — great for selling.
            </p>

            <div style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: C.dim, marginBottom: '6px' }}>
                <span>0</span><span>25</span><span>50</span><span>75</span><span>100</span>
              </div>
              <div style={{ height: '12px', borderRadius: '6px', background: `linear-gradient(to right, ${C.rose}, #c9a84c44, ${C.sage})`, position: 'relative' }}>
                {[
                  { pos: '15%', label: '0–30: Avoid', color: C.rose },
                  { pos: '52%', label: '30–60: Sweet spot', color: C.goldL },
                  { pos: '82%', label: '60+: Check for event risk', color: C.sky },
                ].map(({ pos, label, color }) => (
                  <div key={pos} style={{ position: 'absolute', left: pos, top: '18px', transform: 'translateX(-50%)', fontSize: '9px', color, whiteSpace: 'nowrap', letterSpacing: '0.05em', textAlign: 'center' }}>{label}</div>
                ))}
              </div>
              <div style={{ height: '44px' }} />
            </div>

            {[
              { rank: '0–29',  label: 'Too low',     color: C.rose,  bg: 'rgba(196,122,122,0.1)',  text: "Premium is thin. You're not being compensated for the risk. Wait for a better entry." },
              { rank: '30–60', label: 'Sweet spot',  color: C.sage,  bg: 'rgba(122,171,138,0.1)',  text: 'Ideal for selling puts and calls. Meaningful premium with manageable volatility environment.' },
              { rank: '61–80', label: 'Elevated',    color: C.goldL, bg: 'rgba(201,168,76,0.08)', text: 'More premium — but investigate why IV is high. Could be upcoming earnings or market event.' },
              { rank: '80+',   label: 'Danger zone', color: C.sky,   bg: 'rgba(122,158,196,0.1)',  text: 'Something specific is driving this. Earnings, FDA event, legal risk. Proceed with extreme caution.' },
            ].map(({ rank, label, color, bg, text }) => (
              <div key={rank} style={{ background: bg, borderRadius: '8px', padding: '12px 14px', marginBottom: '8px', display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                <div style={{ fontFamily: "'DM Mono', monospace", fontSize: '15px', color, flexShrink: 0, minWidth: '50px' }}>{rank}</div>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 500, color, marginBottom: '3px' }}>{label}</div>
                  <div style={{ fontSize: '12px', color: C.muted, lineHeight: 1.6 }}>{text}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Section 2: Liquidity */}
      {section === 2 && (
        <div>
          <div style={card}>
            <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '20px', marginBottom: '12px' }}>Liquidity: Why It Matters</div>
            <p style={{ fontSize: '13px', color: C.muted, lineHeight: 1.8, marginBottom: '16px' }}>
              Liquidity is how easily you can enter and exit a position without moving the price against you. In options, poor liquidity means wide bid/ask spreads — and that cost comes directly out of your pocket.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
              <div style={{ background: 'rgba(122,171,138,0.08)', border: `0.5px solid rgba(122,171,138,0.2)`, borderRadius: '8px', padding: '14px' }}>
                <div style={{ fontSize: '12px', color: C.sage, letterSpacing: '0.08em', marginBottom: '8px' }}>LIQUID OPTION</div>
                <div style={{ fontFamily: "'DM Mono', monospace", fontSize: '20px', marginBottom: '6px' }}>$1.45 / $1.48</div>
                <div style={{ fontSize: '12px', color: C.muted }}>$0.03 spread. Near-free to trade. Fill at mid = $1.465</div>
              </div>
              <div style={{ background: 'rgba(196,122,122,0.08)', border: `0.5px solid rgba(196,122,122,0.2)`, borderRadius: '8px', padding: '14px' }}>
                <div style={{ fontSize: '12px', color: C.rose, letterSpacing: '0.08em', marginBottom: '8px' }}>ILLIQUID OPTION</div>
                <div style={{ fontFamily: "'DM Mono', monospace", fontSize: '20px', marginBottom: '6px' }}>$0.85 / $1.40</div>
                <div style={{ fontSize: '12px', color: C.muted }}>$0.55 spread. You give up $27.50 per contract just to get in.</div>
              </div>
            </div>
            {[
              { icon: '✓', label: 'Open Interest ≥ 500 on the strike you want', good: true },
              { icon: '✓', label: 'Daily volume ≥ 100 contracts on the chain', good: true },
              { icon: '✓', label: 'Bid/ask spread ≤ $0.15 per dollar of premium', good: true },
              { icon: '✗', label: "Only 12 open interest — market makers control the price", good: false },
              { icon: '✗', label: "No bid (shows 0.00) — you can't sell what no one wants to buy", good: false },
              { icon: '✗', label: 'Spread wider than the premium itself', good: false },
            ].map(({ icon, label, good }) => (
              <div key={label} style={{ display: 'flex', gap: '10px', padding: '8px 0', borderBottom: `0.5px solid rgba(240,237,230,0.05)`, fontSize: '13px' }}>
                <span style={{ color: good ? C.sage : C.rose, flexShrink: 0 }}>{icon}</span>
                <span style={{ color: good ? C.text : C.muted }}>{label}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Section 3: TOS Scan */}
      {section === 3 && (
        <div>
          <div style={card}>
            <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '20px', marginBottom: '12px' }}>ThinkorSwim Stock Hacker Scan</div>
            <p style={{ fontSize: '13px', color: C.muted, lineHeight: 1.8, marginBottom: '20px' }}>
              Use this scan in TOS to surface Wheel candidates daily. Takes 2 minutes to run. Add it as a saved scan and check it each morning.
            </p>
            <div style={{ background: C.bg3, borderRadius: '10px', padding: '20px', fontFamily: "'DM Mono', monospace", fontSize: '13px', marginBottom: '20px' }}>
              <div style={{ color: C.gold, marginBottom: '12px', fontSize: '11px', letterSpacing: '0.1em' }}>TOS STOCK HACKER — SCAN CRITERIA</div>
              {[
                ['Last',                 '≥ 20',     'Price at least $20'],
                ['Volume',               '≥ 500,000', 'Enough daily stock volume'],
                ['IV Percentile (IVP)',  '≥ 30',     'Options IV is elevated'],
                ['Options Volume',       '≥ 1,000',  'Active options market'],
                ['Market Cap',           '≥ 1B',     'No micro-caps'],
                ['Sector',               'Any',      'Filter by sector if you want focus'],
              ].map(([field, val, note]) => (
                <div key={field} style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 1.5fr', gap: '12px', marginBottom: '8px', alignItems: 'baseline' }}>
                  <span style={{ color: C.goldL }}>{field}</span>
                  <span style={{ color: C.sage }}>{val}</span>
                  <span style={{ color: C.muted, fontSize: '11px' }}>{note}</span>
                </div>
              ))}
            </div>
            <div style={{ background: 'rgba(122,158,196,0.08)', borderRadius: '8px', padding: '14px', fontSize: '13px', color: C.sky, lineHeight: 1.7 }}>
              <strong style={{ color: C.sky }}>How to add in TOS:</strong> Scan tab → Stock Hacker → Add Filter → Select each field above → Save Scan → Name it "Wheel Candidates". Run it each morning and shortlist 3–5 candidates to research further.
            </div>
          </div>
        </div>
      )}

      {/* Section 4: What to Avoid */}
      {section === 4 && (
        <div>
          <div style={card}>
            <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '20px', marginBottom: '16px' }}>What to Avoid</div>
            <p style={{ fontSize: '13px', color: C.muted, lineHeight: 1.8, marginBottom: '20px' }}>
              Knowing what NOT to trade is just as important as knowing what to trade. These are the red flags that should make you immediately move on.
            </p>
            {[
              { flag: 'Earnings within 30 days',              sev: 'hard stop', why: "IV spikes before earnings, collapses after. You'll get crushed by the volatility crush if you're on the wrong side. Never sell naked options into earnings." },
              { flag: 'Stock price under $20',                 sev: 'hard stop', why: 'The premium collected is too small to be meaningful after commissions and risk. A $12 stock with $0.30 premium means $30/contract — not worth your time or capital.' },
              { flag: 'IV Rank under 20',                      sev: 'avoid',     why: "Premium is thin. You're accepting all the same risk for a fraction of the reward. Wait for a better environment." },
              { flag: 'Open interest under 100',               sev: 'avoid',     why: "You're the only one in the room. Market makers will take advantage of you with wide spreads and bad fills." },
              { flag: 'Meme stocks or social media darlings',  sev: 'hard stop', why: 'GameStop, AMC, Reddit-driven names. Impossible to analyze with technicals. Gamma squeezes will blow through any strike you pick.' },
              { flag: "Stocks you wouldn't own at the strike", sev: 'mindset',   why: "On The Wheel, you may get assigned. If you'd be horrified to own 100 shares at $45, don't sell the $45 put." },
              { flag: 'Low-beta stocks that barely move',      sev: 'avoid',     why: 'If the stock has a 0.2 beta and barely moves, the options premium is negligible. Your capital is tied up for almost no return.' },
              { flag: 'Biotech or FDA-event stocks',           sev: 'hard stop', why: 'Binary events. A drug approval or rejection can gap the stock 50% overnight. Not a strategy — it\'s gambling.' },
            ].map(({ flag, why, sev }) => {
              const sevColor = sev === 'hard stop' ? C.rose : sev === 'mindset' ? C.lav : C.sky;
              const sevBg    = sev === 'hard stop' ? 'rgba(196,122,122,0.1)' : sev === 'mindset' ? 'rgba(154,122,196,0.1)' : 'rgba(122,158,196,0.1)';
              return (
                <div key={flag} style={{ padding: '14px 0', borderBottom: `0.5px solid rgba(240,237,230,0.05)` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px', flexWrap: 'wrap' }}>
                    <span style={{ color: C.rose, fontSize: '16px' }}>✗</span>
                    <span style={{ fontSize: '14px', fontWeight: 500 }}>{flag}</span>
                    <span style={tagStyle(sevColor, sevBg)}>{sev.toUpperCase()}</span>
                  </div>
                  <div style={{ fontSize: '12px', color: C.muted, lineHeight: 1.7, paddingLeft: '26px' }}>{why}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Section 5: Quiz */}
      {section === 5 && (
        <div>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '20px', marginBottom: '6px' }}>Chapter Quiz</div>
          <p style={{ fontSize: '13px', color: C.muted, marginBottom: '20px' }}>5 questions · 75% to pass · You've got this.</p>

          {QUIZ.map((q, qi) => (
            <div key={qi} style={card}>
              <div style={{ fontSize: '14px', fontWeight: 500, marginBottom: '14px', lineHeight: 1.6 }}>
                <span style={{ color: C.gold, fontFamily: "'DM Mono', monospace", marginRight: '8px' }}>{qi + 1}.</span>
                {q.q}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {q.options.map((opt, oi) => {
                  const selected  = answers[qi] === oi;
                  const isCorrect = oi === q.correct;
                  let bg = 'transparent', border = C.border, color = C.text;
                  if (submitted) {
                    if (isCorrect)  { bg = 'rgba(122,171,138,0.12)'; border = 'rgba(122,171,138,0.4)'; color = C.sage; }
                    else if (selected) { bg = 'rgba(196,122,122,0.12)'; border = 'rgba(196,122,122,0.4)'; color = C.rose; }
                  } else if (selected) { bg = 'rgba(201,168,76,0.08)'; border = 'rgba(201,168,76,0.4)'; color = C.goldL; }
                  return (
                    <div key={oi} style={{ padding: '10px 14px', borderRadius: '8px', border: `0.5px solid ${border}`, background: bg, color, fontSize: '13px', cursor: submitted ? 'default' : 'pointer', lineHeight: 1.5, transition: 'all 0.15s' }}
                      onClick={() => !submitted && setAnswers(a => ({ ...a, [qi]: oi }))}>
                      {opt}
                    </div>
                  );
                })}
              </div>
              {submitted && (
                <div style={{ marginTop: '10px', fontSize: '12px', color: C.muted, background: C.bg3, borderRadius: '6px', padding: '10px', lineHeight: 1.6 }}>
                  <span style={{ color: C.goldL }}>Lynn says: </span>{q.explain}
                </div>
              )}
            </div>
          ))}

          {!submitted ? (
            <button
              style={{ width: '100%', padding: '14px', background: Object.keys(answers).length === QUIZ.length ? 'rgba(201,168,76,0.1)' : 'transparent', border: `0.5px solid ${Object.keys(answers).length === QUIZ.length ? 'rgba(201,168,76,0.4)' : C.border}`, borderRadius: '10px', color: Object.keys(answers).length === QUIZ.length ? C.goldL : C.muted, fontFamily: "'Jost', sans-serif", fontSize: '14px', cursor: Object.keys(answers).length === QUIZ.length ? 'pointer' : 'not-allowed', letterSpacing: '0.08em' }}
              onClick={() => Object.keys(answers).length === QUIZ.length && submitQuiz()}
              disabled={Object.keys(answers).length < QUIZ.length}>
              {Object.keys(answers).length < QUIZ.length ? `Answer all questions (${Object.keys(answers).length}/${QUIZ.length})` : 'Submit Quiz →'}
            </button>
          ) : (
            <div style={{ textAlign: 'center', padding: '24px', background: C.bg2, borderRadius: '12px', border: `0.5px solid ${score >= 75 ? 'rgba(122,171,138,0.3)' : 'rgba(196,122,122,0.3)'}` }}>
              <div style={{ fontFamily: "'DM Mono', monospace", fontSize: '48px', color: score >= 75 ? C.sage : C.rose, marginBottom: '8px' }}>{score}%</div>
              <div style={{ fontSize: '14px', color: score >= 75 ? C.sage : C.rose, marginBottom: '6px' }}>
                {score >= 75 ? '✓ Chapter passed! Well done.' : '✗ Score 75% to pass. Review the sections and try again.'}
              </div>
              <div style={{ fontSize: '12px', color: C.muted }}>+{score >= 75 ? 120 : 40} XP earned</div>
              {score < 75 && (
                <button style={{ marginTop: '16px', padding: '10px 24px', background: 'rgba(201,168,76,0.08)', border: `0.5px solid rgba(201,168,76,0.3)`, borderRadius: '8px', color: C.goldL, fontFamily: "'Jost', sans-serif", fontSize: '13px', cursor: 'pointer' }}
                  onClick={() => { setSubmitted(false); setAnswers({}); setScore(null); }}>
                  Try Again →
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Bottom nav */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '24px' }}>
        <button style={{ padding: '10px 20px', background: 'transparent', border: `0.5px solid ${C.border}`, borderRadius: '8px', color: C.muted, fontSize: '13px', cursor: section > 0 ? 'pointer' : 'not-allowed', opacity: section > 0 ? 1 : 0.3 }}
          onClick={() => section > 0 && setSection(s => s - 1)}>← Previous</button>
        <button style={{ padding: '10px 20px', background: 'rgba(201,168,76,0.08)', border: `0.5px solid rgba(201,168,76,0.3)`, borderRadius: '8px', color: C.goldL, fontSize: '13px', cursor: section < SECTIONS.length - 1 ? 'pointer' : 'not-allowed', opacity: section < SECTIONS.length - 1 ? 1 : 0.3 }}
          onClick={() => section < SECTIONS.length - 1 && setSection(s => s + 1)}>Next →</button>
      </div>
    </div>
  );
}
